import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  Schedule,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'
import { useEffect, useMemo } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

type FieldConfig<
  TargetDataStructure extends Record<string, unknown>,
  Property extends keyof TargetDataStructure = keyof TargetDataStructure
> = {
  disabled: boolean
  validationErrors: ValidationResult[]
  value: TargetDataStructure[Property]
  onChange: (newValue: TargetDataStructure[Property]) => void
}

type UseInvoiceSettingsWidget = () => {
  fieldsConfig: {
    defaultDueDateDays: FieldConfig<Schedule, 'defaultDueDateDays'> & {
      hidden: boolean
      merchantDefaultValue: number | undefined
    }
    memo: FieldConfig<Schedule, 'memo'> & { hidden: boolean }
    purchaseOrderNumber: FieldConfig<Schedule, 'purchaseOrderNumber'>
    reference: FieldConfig<Schedule, 'reference'> & { hidden: boolean }
    autoIssueInvoices: FieldConfig<Schedule, 'autoIssueInvoices'>
  }
}

export const useInvoiceSettingsWidget: UseInvoiceSettingsWidget = () => {
  const cubeContext = useCubeContext()
  const merchantSettings = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  useEffect(() => {
    if (
      cubeContext.queries.rawData.data.schedule.defaultDueDateDays == null &&
      !merchantSettings.isLoading
    ) {
      const defaultValue = merchantSettings.data?.defaultDueDateDays ?? 30
      cubeContext.mutators.updateData({
        schedule: {
          defaultDueDateDays: defaultValue
        }
      })
    }
  }, [
    merchantSettings.data?.defaultDueDateDays,
    merchantSettings.isLoading,
    cubeContext.queries.rawData.data.schedule.defaultDueDateDays,
    cubeContext.mutators
  ])

  const fieldsConfig = useMemo(() => {
    return {
      defaultDueDateDays: {
        value:
          cubeContext.queries.rawData.data.schedule.defaultDueDateDays ?? 30,
        onChange: (newValue: number | null) =>
          cubeContext.mutators.updateData({
            schedule: {
              defaultDueDateDays: newValue
            }
          }),
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.schedule
            .defaultDueDateDays ?? [],
        disabled:
          !cubeContext.queries.availableFeatures.schedule.defaultDueDateDays
            .available.enabled,
        hidden:
          !cubeContext.queries.availableFeatures.schedule.defaultDueDateDays
            .available.visible,
        merchantDefaultValue: merchantSettings.data?.defaultDueDateDays
      },
      memo: {
        value: cubeContext.queries.rawData.data.schedule.memo ?? '',
        onChange: (newValue: string) =>
          cubeContext.mutators.updateData({
            schedule: {
              memo: newValue
            }
          }),
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.schedule
            .memo ?? [],
        disabled:
          !cubeContext.queries.availableFeatures.schedule.memo.available
            .enabled,
        hidden:
          !cubeContext.queries.availableFeatures.schedule.memo.available.visible
      },
      purchaseOrderNumber: {
        value: cubeContext.queries.rawData.data.schedule.purchaseOrderNumber,
        onChange: (newValue: string) =>
          cubeContext.mutators.updateData({
            schedule: {
              purchaseOrderNumber: newValue
            }
          }),
        validationErrors: [],
        disabled:
          !cubeContext.queries.availableFeatures.schedule.purchaseOrderNumber
            .available.enabled
      },
      reference: {
        hidden:
          !cubeContext.queries.availableFeatures.schedule.reference.available
            .visible,
        value: cubeContext.queries.rawData.data.schedule.reference,
        onChange: (newValue: string) =>
          cubeContext.mutators.updateData({
            schedule: {
              reference: newValue
            }
          }),
        validationErrors: [],
        disabled:
          !cubeContext.queries.availableFeatures.schedule.reference.available
            .enabled
      },
      autoIssueInvoices: {
        value: cubeContext.queries.rawData.data.schedule.autoIssueInvoices,
        onChange: (newValue: boolean) =>
          cubeContext.mutators.updateData({
            schedule: {
              autoIssueInvoices: newValue
            }
          }),
        validationErrors: [],
        disabled:
          !cubeContext.queries.availableFeatures.schedule.autoIssueInvoices
            .available.enabled
      }
    }
  }, [cubeContext, merchantSettings.data?.defaultDueDateDays])

  return { fieldsConfig }
}
